import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Cake, Award, PartyPopper, ChevronRight, X, Sparkles } from "lucide-react";
import api from "@/lib/api";
import { celebrationsThisWeek, companyAnniversary } from "@/lib/celebrations";
import { useAuth } from "@/context/AuthContext";
import { Avatar } from "@/components/Avatar";

// Iter 101 — Content-hash key. Dismiss persists until the current list of
// celebrations changes (new day, new birthday, someone drops out) — at
// which point the banner reappears with a fresh Dismiss button.
const DISMISS_KEY = "celebrations_banner_dismissed_hash";
function contentHash(items, company) {
  // Stable representation: sorted "user_id:type:diff" tuples. `diff`
  // captures "today vs upcoming" so tomorrow's set → different hash.
  const base = items
    .map((c) => `${c.user_id}:${c.type}:${c.when}`)
    .sort()
    .join("|");
  return company ? `${base}#uc:${company.years}:${company.diff}` : base;
}

// Iter 122 — confetti burst on the day of a celebration. CSS-only: a fixed
// set of pieces with staggered delays / horizontal offsets.
const CONFETTI = Array.from({ length: 18 }, (_, i) => ({
  left: `${(i * 5.6 + (i % 3) * 2.5) % 100}%`,
  delay: `${(i % 6) * 0.35}s`,
  duration: `${2.4 + (i % 4) * 0.45}s`,
  color: ["#fde047", "#fca5a5", "#86efac", "#93c5fd", "#f0abfc", "#fdba74"][i % 6],
  size: i % 3 === 0 ? 7 : 5,
}));

export const CelebrationsWidget = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const cel = user?.celebrations;
  const [items, setItems] = useState(null);
  const [avatars, setAvatars] = useState({});
  const company = useMemo(() => companyAnniversary(), []);
  const [dismissedHash, setDismissedHash] = useState(() => {
    try { return window.localStorage.getItem(DISMISS_KEY) || ""; }
    catch { return ""; }
  });

  useEffect(() => {
    if (cel && cel.enabled === false) { setItems([]); return; }
    api.get("/inout")
      .then((r) => {
        const staff = r.data?.staff || [];
        setItems(celebrationsThisWeek(staff, {
          daysAhead: cel?.days_ahead,
          includeAnniversaries: cel?.include_anniversaries !== false,
        }));
        setAvatars(Object.fromEntries(staff.map((s) => [s.user_id, s.avatar_url])));
      })
      .catch(() => setItems([]));
  }, [cel]);

  const hash = useMemo(() => (items ? contentHash(items, company) : ""), [items, company]);

  // Iter 265 — the whole feature can be switched off in Settings → Dashboard.
  if (cel && cel.enabled === false) return null;

  // Iter 122 — the company anniversary can hold the box on its own during
  // September; otherwise it needs at least one staff celebration in the window.
  if (!items || (items.length === 0 && !company)) return null;
  // Iter 101 — hidden until the content changes. `hash === ""` covers the
  // race where localStorage returned something but items haven't loaded.
  if (hash && dismissedHash && hash === dismissedHash) return null;

  const dismiss = () => {
    try { window.localStorage.setItem(DISMISS_KEY, hash); } catch { /* ignore */ }
    setDismissedHash(hash);
  };

  const anyToday = items.some((c) => c.today) || !!company?.today;

  return (
    <div
      data-testid="dashboard-celebrations"
      className={`relative overflow-hidden rounded-2xl bg-gradient-to-br from-violet-600 via-fuchsia-600 to-amber-500 p-[1.5px] shadow-lg ${anyToday ? "celebrate-glow" : ""}`}
    >
      <div className="relative overflow-hidden rounded-2xl bg-gradient-to-br from-violet-700 to-fuchsia-700 px-5 py-4 text-white">
        {/* decorative confetti dots */}
        <div className="pointer-events-none absolute -right-6 -top-6 h-32 w-32 rounded-full bg-white/10 blur-2xl" />
        <div className="pointer-events-none absolute -bottom-8 left-1/3 h-24 w-24 rounded-full bg-amber-300/20 blur-2xl" />

        {anyToday && (
          <div className="pointer-events-none absolute inset-0 overflow-hidden" data-testid="celebrations-confetti" aria-hidden="true">
            {CONFETTI.map((p, i) => (
              <span
                key={i}
                className="celebrate-confetti"
                style={{
                  left: p.left,
                  animationDelay: p.delay,
                  animationDuration: p.duration,
                  backgroundColor: p.color,
                  width: p.size,
                  height: p.size * 1.6,
                }}
              />
            ))}
          </div>
        )}

        {/* Iter 101 — Dismiss chip, top-right. Persists via content hash. */}
        <button
          onClick={dismiss}
          data-testid="celebrations-dismiss"
          title="Dismiss until the celebrations list changes"
          aria-label="Dismiss celebrations banner"
          className="absolute right-3 top-3 z-10 inline-flex h-7 w-7 items-center justify-center rounded-full bg-white/15 text-white/80 ring-1 ring-white/25 transition hover:bg-white/25 hover:text-white focus:outline-none focus:ring-2 focus:ring-white/60"
        >
          <X className="h-3.5 w-3.5" />
        </button>

        <button
          onClick={() => navigate("/in-out")}
          data-testid="celebrations-header"
          className="relative flex w-full items-center gap-2.5 pr-10 text-left"
        >
          <span className="flex h-9 w-9 items-center justify-center rounded-xl bg-white/20 ring-1 ring-white/30 celebrate-icon">
            <PartyPopper className="h-5 w-5" />
          </span>
          <div className="min-w-0">
            <h3 className="font-heading text-base font-bold leading-tight">Current Staff Birthdays and Work Anniversaries</h3>
            <p className="text-xs text-white/70">
              {items.length > 0
                ? `${items.length} celebration${items.length === 1 ? "" : "s"} · tap to open the board`
                : `${company?.name} anniversary month · tap to open the board`}
            </p>
          </div>
          <ChevronRight className="ml-auto h-5 w-5 text-white/60" />
        </button>

        {/* Iter 122 — hard-coded company anniversary (Sept 16). September only. */}
        {company && (
          <div
            data-testid="celebration-company-anniversary"
            title={company.label}
            className={`relative mt-4 flex items-center gap-3 rounded-xl bg-white/20 px-4 py-3 ring-1 ring-white/30 backdrop-blur-sm ${company.today ? "celebrate-glow bg-white/30" : ""}`}
          >
            <span className={`flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-amber-300/30 ring-1 ring-white/40 ${company.today ? "celebrate-icon" : ""}`}>
              <Sparkles className="h-5 w-5 text-amber-200" />
            </span>
            <div className="min-w-0">
              <p className="text-sm font-bold leading-tight">
                {company.name} turns {company.years}
                <span className="ml-2 rounded bg-white/25 px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wide">
                  {company.dateLabel}
                </span>
              </p>
              <p className="text-[11px] text-white/80" data-testid="celebration-company-when">{company.label}</p>
            </div>
          </div>
        )}

        <div className="relative mt-4 flex flex-wrap gap-2.5" data-testid="celebrations-list">
          {items.map((c) => {
            const isBday = c.type === "birthday";
            const Icon = isBday ? Cake : Award;
            const when = c.when;
            return (
              <div
                key={`${c.user_id}-${c.type}`}
                data-testid={`celebration-${c.type}-${c.user_id}`}
                title={c.label}
                className={`flex items-center gap-2.5 rounded-xl bg-white/15 px-3 py-2 ring-1 ring-white/20 backdrop-blur-sm ${c.today ? "celebrate-flash bg-white/25" : ""}`}
              >
                <Avatar name={c.name} src={avatars[c.user_id]} size="sm" />
                <div className="leading-tight">
                  <p className="text-sm font-bold">{(c.name || "").split(/\s+/)[0]}</p>
                  <p className="flex items-center gap-1 text-[11px] text-white/80">
                    <Icon className="h-3 w-3" />
                    {isBday ? "Birthday" : `${c.years}-yr`}{when ? ` · ${when}` : ""}
                  </p>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
};
